import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import '../../styles/mcp.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const emailValid = email.includes('@') && email.includes('.');

  const onFinish = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const user = await login(email, password);

      const go = () => {
        switch (user.role) {
          case 'superadmin':
            navigate('/dashboard/superadmin', { replace: true });
            break;
          case 'agency':
            navigate('/dashboard/agency', { replace: true });
            break;
          case 'client':
            navigate('/client/mcp/map', { replace: true });
            break;
          case 'whitelabel_agency':
            navigate('/dashboard/whitelabel', { replace: true });
            break;
          default:
            navigate('/', { replace: true });
        }
      };
      go();
    } catch (err) {
      console.error('Login error:', err);
      setError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-paper flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[420px]">
        <div className="bg-paper-50 rounded-2xl shadow-card p-8 w-full">
          <h1 className="text-[30px] font-bold text-ink mb-2 font-display">Welcome back.</h1>
          <p className="text-[15px] text-ink-100 mb-6">
            Sign in to your Pathmarkr dashboard.
          </p>

          <form onSubmit={onFinish} className="space-y-3">
            <label htmlFor="signin-email" className="block text-[13px] font-semibold text-ink-100">
              Work email
            </label>
            <input
              id="signin-email"
              type="email"
              autoComplete="email"
              aria-label="Work email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 bg-white border border-paper-300 rounded-lg px-3 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none"
            />

            <label htmlFor="signin-password" className="block text-[13px] font-semibold text-ink-100">
              Password
            </label>
            <input
              id="signin-password"
              type="password"
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-10 bg-white border border-paper-300 rounded-lg px-3 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none"
            />

            <button
              type="submit"
              disabled={!emailValid || !password || loading}
              className="w-full h-10 bg-sun text-ink font-semibold rounded-lg shadow-sun hover:bg-sun-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in…' : 'Sign in →'}
            </button>
          </form>

          {error && (
            <div
              role="alert"
              className="mt-4 flex items-start gap-2 rounded-lg bg-paper-300/60 px-3 py-2 text-[13px] font-semibold text-ink"
            >
              <span aria-hidden="true" className="mt-[1px] text-ink">⚠</span>
              <span>{error}</span>
            </div>
          )}

          <p className="mt-6 text-[13px] text-ink-100">
            <span className="font-semibold text-ink">New to Pathmarkr?</span>{' '}
            Email{' '}
            <a href="mailto:support@pathmarkr.com" className="font-bold text-brand hover:underline">
              support@pathmarkr.com
            </a>{' '}
            to request an account. Our team will create your account and guide you through the onboarding process.
          </p>

          <p className="mt-3 text-[12px] text-ink-300">
            Your credentials are encrypted in transit and never shared.
          </p>
        </div>

        <p className="mt-6 text-center text-[12px] text-ink-300">
          Already have an account? Use the same credentials to sign back in.
        </p>
      </div>
    </main>
  );
}

export default Login;
